import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/store/auth-store'
import { PageLoader } from '@/components/ui/page-loader'
import { Pagination } from '@/components/ui/pagination'
import { TruncationNotice } from '@/components/ui/truncation-notice'
import { estimatesService } from '@/services/estimates-service'
import { NewEstimateWizard } from '@/components/estimate/new-estimate-wizard'
import { hasRealOptions } from '@/lib/estimate-flags'
import { matchesEstimateSearch } from '@/lib/list-filters'
import type { Estimate, EstimateStatus } from '@/types'

const PAGE_SIZE = 25

// Map backend statuses to display names and CSS classes
const STATUS_MAP: Record<EstimateStatus, { label: string; cssClass: string }> = {
  draft: { label: 'Draft', cssClass: 'in-progress' },
  submitted_for_approval: { label: 'Submitted for Approval', cssClass: 'ready' },
  approved: { label: 'Approved', cssClass: 'approved' },
  rejected: { label: 'Rejected', cssClass: 'rejected' },
}

function formatDate(date: string) {
  return new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

/** Public Works / Options indicators, shown next to the estimate name. */
function EstimateFlagPills({ estimate }: { estimate: Estimate }) {
  const options = hasRealOptions(estimate.optionsData)
  const superseded = !!estimate.supersededByRevision
  if (!estimate.isPublicWorks && !options && !superseded) return null

  return (
    <>
      {estimate.isPublicWorks && (
        <span className="est-flag-pill est-flag-pw" title="Public works project">PW</span>
      )}
      {options && (
        <span className="est-flag-pill est-flag-opt" title="Includes optional add-ons">Options</span>
      )}
      {/* A revision has replaced this one, and both are listed — without the pill
          there is no way to tell which row carries the current numbers. */}
      {superseded && (
        <span
          className="est-flag-pill est-flag-superseded"
          title="A revision has replaced this version — it keeps the earlier numbers"
        >
          Revised
        </span>
      )}
    </>
  )
}

function daysSince(date: string): number {
  const submitted = new Date(date)
  const now = new Date()
  const diffMs = now.getTime() - submitted.getTime()
  return Math.floor(diffMs / (1000 * 60 * 60 * 24))
}

export function EstimatesListPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const user = useAuthStore((s) => s.user)
  const [estimates, setEstimates] = useState<Estimate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [wizardOpen, setWizardOpen] = useState(searchParams.get('new') === '1')
  const [search, setSearch] = useState('')
  const [waitingOpen, setWaitingOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'all' | 'unit-pricing' | 'public-works'>('all')
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [take, setTake] = useState(1000)
  const [loadingAll, setLoadingAll] = useState(false)

  const userRole = user?.role || ''
  const isManagerOrAbove = userRole === 'MANAGER' || userRole === 'ADMIN' || userRole === 'SUPERADMIN'
  const isAdmin = userRole === 'ADMIN' || userRole === 'SUPERADMIN'

  useEffect(() => {
    loadEstimates()
  }, [])

  async function loadEstimates(takeArg = take) {
    try {
      setIsLoading(true)
      const result = await estimatesService.list(undefined, 0, takeArg)
      setEstimates(result.estimates || [])
      setTotal(result.total ?? (result.estimates?.length || 0))
    } catch {
      setEstimates([])
    } finally {
      setIsLoading(false)
    }
  }

  async function handleShowAll() {
    if (loadingAll || total <= take) return
    setLoadingAll(true)
    // Fetch directly (not via loadEstimates) so a failed Show-all does NOT wipe
    // the visible list — loadEstimates' catch sets [] on error. Update take only
    // after success so a failure leaves the button usable for a retry.
    try {
      const result = await estimatesService.list(undefined, 0, total)
      setEstimates(result.estimates || [])
      setTotal(result.total ?? (result.estimates?.length || 0))
      setTake(total)
    } catch (err) {
      console.error('Failed to load all estimates:', err)
    } finally {
      setLoadingAll(false)
    }
  }

  async function handleDuplicate(e: React.MouseEvent, estId: string) {
    e.stopPropagation()
    if (duplicatingId) return
    setDuplicatingId(estId)
    try {
      const copy = await estimatesService.duplicate(estId)
      setEstimates((prev) => {
        const idx = prev.findIndex((e) => e.id === estId)
        if (idx === -1) return [...prev, copy]
        const next = [...prev]
        next.splice(idx + 1, 0, copy)
        return next
      })
    } catch {
      // silent — API failure leaves list unchanged
    } finally {
      setDuplicatingId(null)
    }
  }

  async function handleDelete(id: string) {
    if (deletingId) return
    setDeletingId(id)
    try {
      await estimatesService.remove(id)
      setEstimates((prev) => prev.filter((e) => e.id !== id))
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Failed to delete estimate. Please try again.')
    } finally {
      setDeletingId(null)
      setDeleteConfirmId(null)
    }
  }

  // Split: waiting for approval goes to top section (managers only), rest to main table
  const waitingEstimates = isManagerOrAbove
    ? estimates.filter((e) => e.status === 'submitted_for_approval')
    : []
  const tableEstimates = isManagerOrAbove
    ? estimates.filter((e) => e.status !== 'submitted_for_approval')
    : estimates

  // Tab + search filter for main table
  const filteredEstimates = tableEstimates.filter((e) => {
    // Tab filter
    if (activeTab === 'unit-pricing' && !e.isUnitPricing) return false
    if (activeTab === 'public-works' && !e.isPublicWorks) return false

    // Search filter — name, client and the estimator who created it.
    return matchesEstimateSearch(e, search)
  })

  const totalPages = Math.ceil(filteredEstimates.length / PAGE_SIZE)
  const pagedEstimates = filteredEstimates.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <div>
      {/* Page Header */}
      <div className="est-list-head">
        <h1 className="est-list-title">Estimates</h1>
        <button onClick={() => setWizardOpen(true)} className="btn">+ Create New Estimate</button>
      </div>

      {/* ── Waiting for Approval Section (managers/admins only) ── */}
      {isManagerOrAbove && waitingEstimates.length > 0 && (
        <div className="prop-pending-section" style={{ marginBottom: '24px' }}>
          <div className="prop-pending-header prop-pending-toggle" onClick={() => setWaitingOpen(!waitingOpen)}>
            <div className="prop-pending-icon" style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--red)' }}>
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <h2 className="prop-pending-title">Waiting for Approval</h2>
              <p className="prop-pending-subtitle">{waitingEstimates.length} estimate{waitingEstimates.length !== 1 ? 's' : ''} submitted and awaiting your review</p>
            </div>
            <svg className={`prop-pending-chevron ${waitingOpen ? 'open' : ''}`} width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>

          {waitingOpen && (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Estimate Name</th>
                  <th>Client</th>
                  <th>Estimator</th>
                  {/* The same two dates as the main table, so one estimate cannot
                      appear to carry different dates in the two sections. A
                      revision in the queue is worth spotting before approving it. */}
                  <th>Created</th>
                  <th>Revised</th>
                  <th>Submitted</th>
                  <th style={{ width: '100px' }}></th>
                </tr>
              </thead>
              <tbody>
                {waitingEstimates.map((est) => {
                  const days = daysSince(est.updatedAt)
                  return (
                    <tr
                      key={est.id}
                      className="est-list-row"
                      onClick={() => navigate(`/estimates/${est.id}`)}
                    >
                      <td style={{ fontWeight: 500, color: 'var(--s900)' }}>
                        {est.title}
                        <EstimateFlagPills estimate={est} />
                      </td>
                      <td>{est.clientName || est.clientCompany || '—'}</td>
                      <td>{est.createdByUser?.displayName || '—'}</td>
                      <td>{formatDate(est.originalCreatedAt || est.createdAt)}</td>
                      <td style={{ color: est.revisedAt ? undefined : 'var(--s400)' }}>
                        {est.revisedAt ? formatDate(est.revisedAt) : '—'}
                      </td>
                      <td>
                        {formatDate(est.updatedAt)}
                        {days > 3 && (
                          <span className="est-overdue-text">{days} days ago</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="est-review-btn"
                          onClick={(e) => { e.stopPropagation(); navigate(`/estimates/${est.id}`) }}
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: '16px' }}>
        <div className="search-wrap">
          <input
            type="text"
            className="search-input"
            placeholder="Search by estimate, client or estimator..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          />
          <span className="search-icon">
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </span>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="est-list-tabs">
        <button className={`est-list-tab ${activeTab === 'all' ? 'active' : ''}`} onClick={() => { setActiveTab('all'); setPage(1) }}>
          All Estimates
        </button>
        <button className={`est-list-tab ${activeTab === 'unit-pricing' ? 'active' : ''}`} onClick={() => { setActiveTab('unit-pricing'); setPage(1) }}>
          Unit Pricing
        </button>
        <button className={`est-list-tab ${activeTab === 'public-works' ? 'active' : ''}`} onClick={() => { setActiveTab('public-works'); setPage(1) }}>
          Public Works
        </button>
      </div>

      <TruncationNotice shown={estimates.length} total={total} onShowAll={handleShowAll} loading={loadingAll} />

      {/* Table */}
      <div className="card" style={{ marginTop: '15px' }}>
        {isLoading ? (
          <PageLoader />
        ) : filteredEstimates.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <svg style={{ width: '48px', height: '48px', margin: '0 auto 12px', color: 'var(--s300)' }} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z" />
            </svg>
            {search ? (
              <>
                <p style={{ fontWeight: 500, color: 'var(--s500)' }}>No estimates found</p>
                <p style={{ fontSize: '13px', color: 'var(--s400)', marginTop: '4px' }}>Try a different search term.</p>
              </>
            ) : activeTab === 'unit-pricing' ? (
              <p style={{ fontWeight: 500, color: 'var(--s500)' }}>You have no Unit Pricing Estimates.</p>
            ) : activeTab === 'public-works' ? (
              <p style={{ fontWeight: 500, color: 'var(--s500)' }}>You have no Public Works Estimates.</p>
            ) : (
              <p style={{ fontWeight: 500, color: 'var(--s500)' }}>You have no Estimates yet.</p>
            )}
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Client</th>
                {/* Who wrote the estimate. Only for managers and admins — an
                    estimator sees nothing here but their own work, so the column
                    would repeat their own name on every row. */}
                {isManagerOrAbove && <th>Created By</th>}
                {/* Two dates, labelled. One column read as wrong on a revised bid:
                    the revision is created the day someone hits Revise, while the
                    proposal keeps the date the bid first went out. */}
                <th>Created</th>
                <th>Revised</th>
                <th>Status</th>
                <th style={{ width: '40px' }}></th>
              </tr>
            </thead>
            <tbody>
              {pagedEstimates.map((est) => {
                const status = STATUS_MAP[est.status] || STATUS_MAP.draft
                return (
                  <tr
                    key={est.id}
                    className="est-list-row"
                    onClick={() => navigate(`/estimates/${est.id}`)}
                  >
                    <td style={{ fontWeight: 500, color: 'var(--s900)' }}>
                      {est.title}
                      <EstimateFlagPills estimate={est} />
                      {duplicatingId === est.id && <span className="est-dup-spinner" />}
                    </td>
                    <td>{est.clientName || est.clientCompany || '—'}</td>
                    {isManagerOrAbove && <td>{est.createdByUser?.displayName || '—'}</td>}
                    <td>{formatDate(est.originalCreatedAt || est.createdAt)}</td>
                    <td style={{ color: est.revisedAt ? undefined : 'var(--s400)' }}>
                      {est.revisedAt ? formatDate(est.revisedAt) : '—'}
                    </td>
                    <td>
                      <span className={`est-status ${status.cssClass}`}>{status.label}</span>
                    </td>
                    <td className="est-kebab-cell" onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <button
                          className="est-dup-icon-btn"
                          onClick={(e) => handleDuplicate(e, est.id)}
                          disabled={!!duplicatingId}
                          title="Duplicate estimate"
                        >
                          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                            <rect x="9" y="9" width="13" height="13" rx="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        {isAdmin && (
                          <button
                            className="est-dup-icon-btn"
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(est.id) }}
                            disabled={!!deletingId}
                            title="Delete estimate"
                            style={{ color: 'var(--red)' }}
                          >
                            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                              <polyline points="3 6 5 6 21 6" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M10 11v6M14 11v6" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={filteredEstimates.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
          </table>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmId && (
        <div className="est-modal-overlay" onClick={() => !deletingId && setDeleteConfirmId(null)}>
          <div className="est-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="est-modal-header">
              <h3 className="est-modal-title">Delete Estimate</h3>
              <button className="est-modal-close" onClick={() => !deletingId && setDeleteConfirmId(null)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ color: 'var(--s700)', fontSize: '14px', lineHeight: '1.5' }}>
                Are you sure you want to delete this estimate? If a draft proposal is attached, it will also be deleted. This action cannot be undone.
              </p>
            </div>
            <div style={{ padding: '0 24px 20px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                className="btn-secondary"
                onClick={() => setDeleteConfirmId(null)}
                disabled={!!deletingId}
              >
                Cancel
              </button>
              <button
                className="btn"
                style={{ background: 'var(--red)', borderColor: 'var(--red)' }}
                onClick={() => handleDelete(deleteConfirmId)}
                disabled={!!deletingId}
              >
                {deletingId ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <NewEstimateWizard
        open={wizardOpen}
        onOpenChange={(open) => {
          setWizardOpen(open)
          if (!open && searchParams.has('new')) {
            searchParams.delete('new')
            searchParams.delete('rfpClientName')
            searchParams.delete('rfpAddress')
            searchParams.delete('rfpEmail')
            searchParams.delete('rfpPhone')
            searchParams.delete('rfpScopeOfWork')
            setSearchParams(searchParams, { replace: true })
          }
        }}
        prefillClientName={searchParams.get('rfpClientName') || undefined}
        prefillAddress={searchParams.get('rfpAddress') || undefined}
        prefillEmail={searchParams.get('rfpEmail') || undefined}
        prefillPhone={searchParams.get('rfpPhone') || undefined}
        prefillScopeOfWork={searchParams.get('rfpScopeOfWork') || undefined}
      />
    </div>
  )
}
